import { useMemo, Fragment, useState, useEffect } from 'react';
import { formatWeekDayShort } from '@/lib/format-utils';
import { Reservation, ServiceBooking } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { TagIcon } from '@/lib/tag-utils';
import EditReservationBookingRow from './edit-reservation-booking-row';
import EditReservationBookingGroupRow from './edit-reservation-booking-group-row';
import { Button } from '@mantine/core';
import { IconCategory, IconList, IconSortAscending, IconSortDescending, IconStack2 } from '@tabler/icons-react';
import ReservationMenu from '@/components/reservation-menu';

interface Props {
    bookings: ServiceBooking[];
    reservation?: Reservation;
    selectedIds?: number[];
    onToggle?: (id: number) => void;
    onToggleId?: (id: number) => void;
    showCategory?: boolean;
    showServiceOrder?: boolean;
    showReservation?: boolean;
    showClientName?: boolean;
    showPrice?: boolean;
    showCost?: boolean;
    showPaymentStatus?: boolean;
    showPaxs?: boolean;
    showPaxName?: boolean;
    showHour?: boolean;
    leftAction?: (booking: ServiceBooking) => void;
    leftSection?: React.ReactNode;
    rightAction?: (booking: ServiceBooking) => void;
    rightSection?: React.ReactNode | ((booking: ServiceBooking) => React.ReactNode);
    editable?: boolean;
    firstTd?: React.ReactNode | ((booking: ServiceBooking) => React.ReactNode);
    hideServicePackage?: boolean;
    onAddServicesClick?: () => void;
    disabled?: boolean;
}

const sortBookingsByDate = (list: ServiceBooking[], asc: boolean) => {
    return [...list].sort((a, b) => {
        const isPkgA = a.service_data?.child_services?.length > 0 || a.category === 'service_group';
        const isPkgB = b.service_data?.child_services?.length > 0 || b.category === 'service_group';
        if (isPkgA !== isPkgB) {
            return isPkgA ? -1 : 1;
        }

        const hasDateA = !!a.start_day;
        const hasDateB = !!b.start_day;
        if (hasDateA !== hasDateB) {
            return hasDateA ? 1 : -1;
        }

        if (hasDateA && hasDateB) {
            const dayA = a.start_day!;
            const dayB = b.start_day!;

            if (dayA !== dayB) {
                return asc ? dayA.localeCompare(dayB) : dayB.localeCompare(dayA);
            }

            const hourA = a.start_hour || (asc ? '99:99' : '00:00');
            const hourB = b.start_hour || (asc ? '99:99' : '00:00');

            if (hourA !== hourB) {
                return asc ? hourA.localeCompare(hourB) : hourB.localeCompare(hourA);
            }
        }

        return asc ? a.id - b.id : b.id - a.id;
    });
};

const getBookingExactServiceKey = (booking: ServiceBooking): string => {
    const serviceId = booking.service_id ?? booking.service_data?.id ?? 'none';
    const startDay = booking.start_day || '';
    const endDay = booking.end_day || '';
    const startHour = booking.start_hour || '';
    const endHour = booking.end_hour || '';
    const providerId = booking.selected_provider_id ?? booking.service_data?.providers?.[0]?.id ?? '';

    const sortedBookingAttrs = (booking.self_attrs || [])
        .map(a => `${a.key}:${a.value}`)
        .sort()
        .join(',');

    const sortedServiceAttrs = (booking.service_data?.self_attrs || [])
        .map(a => `${a.key}:${a.value}`)
        .sort()
        .join(',');

    return `${serviceId}|${startDay}|${endDay}|${startHour}|${endHour}|${providerId}|${sortedBookingAttrs}|${sortedServiceAttrs}`;
};

type BookingCluster =
    | { type: 'single'; booking: ServiceBooking }
    | { type: 'compressed_group'; key: string; bookings: ServiceBooking[] };

const buildClusters = (
    categoryBookings: ServiceBooking[],
    compressIdentical: boolean
): BookingCluster[] => {
    if (!compressIdentical) {
        return categoryBookings.map(b => ({
            type: 'single',
            booking: b,
        }));
    }

    const clustersList: BookingCluster[] = [];
    const groupMap = new Map<string, ServiceBooking[]>();
    const groupOrder: string[] = [];

    categoryBookings.forEach(booking => {
        const key = getBookingExactServiceKey(booking);
        if (!groupMap.has(key)) {
            groupMap.set(key, []);
            groupOrder.push(key);
        }
        groupMap.get(key)!.push(booking);
    });

    groupOrder.forEach(key => {
        const items = groupMap.get(key)!;
        if (items.length > 4) {
            clustersList.push({
                type: 'compressed_group',
                key,
                bookings: items,
            });
        } else {
            items.forEach(b => {
                clustersList.push({
                    type: 'single',
                    booking: b,
                });
            });
        }
    });

    return clustersList;
};

export default function EditReservationBookings({
    bookings,
    reservation,
    selectedIds,
    onToggle,
    onToggleId,
    showCategory = true,
    showServiceOrder = true,
    showReservation = true,
    showClientName = true,
    showPrice = false,
    showCost = false,
    showPaymentStatus = true,
    showPaxs = true,
    showPaxName = true,
    showHour = true,
    leftAction,
    leftSection,
    rightAction,
    rightSection,
    editable = false,
    firstTd,
    hideServicePackage = true,
    onAddServicesClick,
    disabled = false
}: Props
): JSX.Element {
    const { t } = useTranslation();
    const [groupByCategory, setGroupByCategory] = useState(() => {
        const stored = localStorage.getItem('turizi_group_by_category');
        return stored !== null ? stored === 'true' : true;
    });
    const [sortAscending, setSortAscending] = useState(() => {
        const stored = localStorage.getItem('turizi_sort_ascending');
        return stored !== null ? stored === 'true' : true;
    });
    const [compressIdentical, setCompressIdentical] = useState(() => {
        const stored = localStorage.getItem('turizi_compress_identical_bookings');
        return stored !== null ? stored === 'true' : true;
    });
    const [expandedGroupKeys, setExpandedGroupKeys] = useState<Record<string, boolean>>({});

    useEffect(() => {
        localStorage.setItem('turizi_group_by_category', String(groupByCategory));
    }, [groupByCategory]);

    useEffect(() => {
        localStorage.setItem('turizi_sort_ascending', String(sortAscending));
    }, [sortAscending]);

    useEffect(() => {
        localStorage.setItem('turizi_compress_identical_bookings', String(compressIdentical));
    }, [compressIdentical]);


    const groupedbookings = useMemo(() => {
        let filtered = bookings || [];

        if (hideServicePackage) {
            filtered = filtered.filter(booking => !(booking.service_data?.child_services?.length > 0));
        }

        const sortedBookings = sortBookingsByDate(filtered, sortAscending);
        const grouped = new Map<string, ServiceBooking[]>();

        if (groupByCategory) {
            sortedBookings.forEach((booking) => {
                const category = booking.service_data.category || 'other';
                if (!grouped.has(category)) {
                    grouped.set(category, []);
                }
                grouped.get(category)!.push(booking);
            });

            // Sort categories: "service_group" first, "stay" second, then the rest alphabetically
            const sortedEntries = Array.from(grouped.entries()).sort(([catA], [catB]) => {
                if (catA === 'service_group') return -1;
                if (catB === 'service_group') return 1;
                if (catA === 'stay') return -1;
                if (catB === 'stay') return 1;
                return catA.localeCompare(catB);
            });

            return new Map<string, ServiceBooking[]>(sortedEntries);
        } else {
            grouped.set('all', sortedBookings);
            return grouped;
        }
    }, [bookings, hideServicePackage, groupByCategory, sortAscending]);

    const allGroupKeys = useMemo(() => {
        if (!compressIdentical) return [];
        const keys: string[] = [];
        groupedbookings.forEach((catBookings) => {
            const map = new Map<string, number>();
            catBookings.forEach((b) => {
                const k = getBookingExactServiceKey(b);
                map.set(k, (map.get(k) || 0) + 1);
            });
            map.forEach((count, k) => {
                if (count > 4) {
                    keys.push(k);
                }
            });
        });
        return keys;
    }, [groupedbookings, compressIdentical]);

    const hasAnyGroups = allGroupKeys.length > 0;
    const allExpanded = hasAnyGroups && allGroupKeys.every((k) => expandedGroupKeys[k]);

    const toggleAllGroups = () => {
        if (allExpanded) {
            setExpandedGroupKeys({});
        } else {
            const next: Record<string, boolean> = {};
            allGroupKeys.forEach((k) => {
                next[k] = true;
            });
            setExpandedGroupKeys(next);
        }
    };

    return (
        <div className='space-y-1'>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <ReservationMenu
                            reservation={reservation}
                            disabled={disabled}
                        />
                        {onAddServicesClick && !disabled && (
                            <Button
                                size="xs"
                                variant="filled"
                                color="black"
                                radius="xl"
                                disabled={!reservation.client_business}
                                onClick={onAddServicesClick}
                            >
                                {t('add_services')}
                            </Button>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        size="xs"
                        color={compressIdentical ? 'indigo' : 'black'}
                        variant={compressIdentical ? 'light' : 'subtle'}
                        leftSection={<IconStack2 size={16} />}
                        onClick={() => setCompressIdentical(prev => !prev)}
                        title={compressIdentical ? t('uncompress_identical') : t('compress_identical')}
                    >
                        {!compressIdentical ? t('compress_identical') : t('uncompress_identical')}
                    </Button>

                    {compressIdentical && hasAnyGroups && (
                        <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            onClick={toggleAllGroups}
                        >
                            {allExpanded ? t('collapse_all') : t('expand_all')}
                        </Button>
                    )}

                    <Button
                        size="xs"
                        color='black'
                        variant='subtle'
                        leftSection={groupByCategory ? <IconCategory size={16} /> : <IconList size={16} />}
                        onClick={() => setGroupByCategory(prev => !prev)}
                    >
                        {!groupByCategory ? t('group_by_category') : t('ungrouped')}
                    </Button>
                    <Button
                        size="xs"
                        variant="default"
                        onClick={() => setSortAscending(prev => !prev)}
                    >
                        {sortAscending ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />}
                    </Button>
                </div>
            </div>

            {Array.from(groupedbookings.entries()).map(([category, categoryBookings]) => {
                const resolvedFirstTd = firstTd
                    ? (typeof firstTd === 'function' ? firstTd(categoryBookings[0]) : firstTd)
                    : null;
                const clusters = buildClusters(categoryBookings, compressIdentical);

                return (
                    <div key={category} className="space-y-1">
                        {/* category header */}
                        {showCategory && groupByCategory && (
                            <div className="flex items-center gap-1.5 pt-4 pb-0.5 px-1 select-none">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                    {t(`${category}_plural`)}
                                </span>
                                <span className="text-[11px] font-medium text-slate-300">
                                    · {categoryBookings.length}
                                </span>
                            </div>
                        )}

                        <div className="flex flex-col lg:flex-row gap-1 items-stretch">
                            {resolvedFirstTd && (
                                <div className="w-full lg:w-[120px] shrink-0 bg-gray-50/50 p-3 rounded-xl border border-gray-250/60 flex items-center justify-center text-center">
                                    {resolvedFirstTd}
                                </div>
                            )}

                            <div className="flex-grow flex flex-col gap-1 min-w-0">
                                {(() => {
                                    let prevStartDay: string | null | undefined = undefined;
                                    return clusters.map((cluster) => {
                                        const currentStartDay = cluster.type === 'single'
                                            ? cluster.booking.start_day || null
                                            : cluster.bookings[0]?.start_day || null;
                                        const showDateSeparation = !firstTd && (prevStartDay === undefined || currentStartDay !== prevStartDay);
                                        prevStartDay = currentStartDay;

                                        const clusterKey = cluster.type === 'single' ? cluster.booking.id! : cluster.key;

                                        return (
                                            <Fragment key={clusterKey}>
                                                {showDateSeparation && currentStartDay && (
                                                    <div className="px-1 -mb-1 first:pt-0 select-none">
                                                        <span className="text-[10px] font-medium text-slate-400 capitalize">
                                                            {formatWeekDayShort(currentStartDay)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div>
                                                    {cluster.type === 'single' ? (
                                                        <EditReservationBookingRow
                                                            booking={cluster.booking}
                                                            reservation={reservation}
                                                            selected={selectedIds?.includes(cluster.booking.id!)}
                                                            onToggle={onToggle || onToggleId}
                                                            showServiceOrder={showServiceOrder}
                                                            showReservation={showReservation}
                                                            showClientName={showClientName}
                                                            showPrice={showPrice}
                                                            showCost={showCost}
                                                            showPaymentStatus={showPaymentStatus}
                                                            showPaxs={showPaxs}
                                                            showPaxName={showPaxName}
                                                            showHour={showHour}
                                                            leftAction={leftAction}
                                                            leftSection={leftSection}
                                                            rightAction={rightAction}
                                                            rightSection={rightSection}
                                                            editable={editable}
                                                            hideServicePackage={hideServicePackage}
                                                            slim={false}
                                                        />
                                                    ) : (
                                                        <EditReservationBookingGroupRow
                                                            groupKey={cluster.key}
                                                            bookings={cluster.bookings}
                                                            reservation={reservation}
                                                            selectedIds={selectedIds}
                                                            onToggle={onToggle}
                                                            onToggleId={onToggleId}
                                                            showServiceOrder={showServiceOrder}
                                                            showReservation={showReservation}
                                                            showClientName={showClientName}
                                                            showPrice={showPrice}
                                                            showCost={showCost}
                                                            showPaymentStatus={showPaymentStatus}
                                                            showPaxs={showPaxs}
                                                            showPaxName={showPaxName}
                                                            showHour={showHour}
                                                            leftAction={leftAction}
                                                            leftSection={leftSection}
                                                            rightAction={rightAction}
                                                            rightSection={rightSection}
                                                            editable={editable}
                                                            hideServicePackage={hideServicePackage}
                                                            isExpanded={Boolean(expandedGroupKeys[cluster.key])}
                                                            onToggleExpand={() => {
                                                                setExpandedGroupKeys(prev => ({
                                                                    ...prev,
                                                                    [cluster.key]: !prev[cluster.key],
                                                                }));
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </Fragment>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div >
    );
}
